import { useRef, useState, useEffect, Dispatch } from "react";
import { SwiperProps } from "swiper/react";
import { Swiper as SwiperType } from "swiper/types";
import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  FreeMode,
  Keyboard,
  Mousewheel,
} from "swiper/modules";

import debounce from "lodash-es/debounce";

const swiperModules = [
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  FreeMode,
  Keyboard,
  Mousewheel,
];
export const breakpointValues = {
  mobile: "280",
  tablet: "768",
};

function setEdges(
  isBeginning: boolean,
  setBeginning: Dispatch<boolean>,
  isEnd: boolean,
  setEnd: Dispatch<boolean>
): void {
  isEnd ? setEnd(true) : setEnd(false);
  isBeginning ? setBeginning(true) : setBeginning(false);
}

export const useConfig = ({
  navBtns,
  paginationClass,
  showPagination,
  forcePagination,
  itemSpacing,
  initialIndex,
  centerSlides,
  randStr,
  cssMode,
  onItemChange,
  onAfterInit,
  onResize: _onResize,
  disableMouseWheel,
  leftPadding,
  rightPadding,
  disableFreeMode = false,
  handleItemImpression,
  _enabled,
  onBeforeResize,
  onCarouselTouchStart,
  onCarouselScroll,
  carouselScrollDebounceSpeed = 50,
  carouselScrollDebounceOptions,
}: any) => {
  const swiperInstance = useRef<SwiperType | null>(null);

  const [breakpoint, setBreakpoint] = useState();
  const [isEnd, setEnd] = useState<boolean>(false);
  const [isBeginning, setBeginning] = useState<boolean>(false);
  const [isLocked, setIsLocked] = useState<boolean>(false);
  const [timeoutId, setTimeoutId] = useState<null | number>(null);

  const onResize = debounce((swiper) => {
    const { isBeginning, isEnd, isLocked } = swiper;
    setEdges(isBeginning, setBeginning, isEnd, setEnd);
    setIsLocked(isLocked);
    typeof _onResize === "function" && _onResize(swiper);
  }, 200);

  const swiperSpeed = 200;

  /* HANDLERS */

  // Visible Impressions
  function handleVisibleImpressions(visibleItems: number[]) {
    visibleItems.forEach((item) => handleItemImpression(item, visibleItems));
  }

  // CarouselScroll
  let lastDistance;
  const onSetTranslate = onCarouselScroll
    ? debounce(
        (swiper, e) => {
          const scrollDistance =
            typeof lastDistance === "number"
              ? swiper.translate - lastDistance
              : swiper.translate;
          onCarouselScroll &&
            onCarouselScroll({ swiperInstance: swiper, e, scrollDistance });
          lastDistance = swiper.translate ?? 0;
        },
        carouselScrollDebounceSpeed,
        carouselScrollDebounceOptions
      )
    : undefined;

  // TouchStart
  const onTouchStart = typeof onCarouselTouchStart === "function" ? onCarouselTouchStart : undefined;
  
  // (swiper, e) => {
  //   if (e.target.classList.contains("swiper-ignore") || e?.target?.offsetParent?.classList.contains("swiper-ignore")) {
  //     swiper.disable()
  //   }
  // }

  let _cssMode: boolean = cssMode;
  if (cssMode === "false") _cssMode = false;
  if (cssMode === "true") _cssMode = true;

  const breakpoints = {
    [breakpointValues.mobile]: {
      centeredSlides: typeof centerSlides === "boolean" ? centerSlides : true,
      centeredSlidesBounds: true,
      freeMode: { enabled: false, sticky: true, momentumBounce: false },
    },
    [breakpointValues.tablet]: {
      centeredSlides: typeof centerSlides === "boolean" ? centerSlides : false,
      centeredSlidesBounds: true,
      grabCursor: true,
      freeMode: { enabled: !disableFreeMode, sticky: false, momentumBounce: false },
    },
  };

  /** Shared config */
  const focusableElements =
    "input, select, option, textarea, video, label, button";
  const a11y = {
    prevSlideMessage: "Previous item",
    nextSlideMessage: "Next item",
  };

  const touchPrevention = {
    preventInteractionOnTransition: true,
    // noSwipingSelector: "button",
    slideToClickedSlide: false,
  };

  const _leftPadding = typeof leftPadding === "number" ? { slidesOffsetBefore: leftPadding } : undefined;
  const _rightPadding = typeof rightPadding === "number" ? { slidesOffsetAfter: rightPadding } : undefined;

  const [config, setConfig] = useState<SwiperProps>({
    modules: swiperModules,
    breakpoints,
    a11y,
    navigation: {
      nextEl: `#${navBtns.nextElId}`,
      prevEl: `#${navBtns.prevElId}`,
    },
    pagination:
      showPagination || forcePagination
        ? {
            dynamicBullets: true,
            clickable: true,
            el: `.${paginationClass}`,
          }
        : { enabled: false },
    slidesPerView: "auto",
    spaceBetween: itemSpacing,
    initialSlide: initialIndex,
    keyboard: { enabled: false, onlyInViewport: true },
    mousewheel: {
      eventsTarget: `.cui__carousel.swiper-slide.cui__carousel_${randStr.current}`,
      enabled: !disableMouseWheel,
    },
    speed: swiperSpeed,
    focusableElements,
    resizeObserver: true,
    watchSlidesProgress: true,
    ..._leftPadding,
    ..._rightPadding,
    cssMode: _cssMode,
    ...touchPrevention,
    onSwiper(swiper: SwiperType) {
      const { isBeginning, isEnd, isLocked } = swiper;
      setEdges(isBeginning, setBeginning, isEnd, setEnd);
      setIsLocked(isLocked);
      swiperInstance.current = swiper;
    },
    onTouchStart,
    onBreakpoint(swiper: SwiperType) {
      const { currentBreakpoint } = swiper;
      setBreakpoint(currentBreakpoint);
    },
    onSlideChange({
      isEnd,
      isBeginning,
      activeIndex,
      visibleSlidesIndexes = [],
      realIndex,
      ...swiper
    }: SwiperType & { visibleSlidesIndexes?: number[] }) {
      if (typeof onItemChange === "function") {
        onItemChange(activeIndex);
      }
      if (typeof handleItemImpression === "function") {
        if (visibleSlidesIndexes.length)
          handleVisibleImpressions(visibleSlidesIndexes);
      }
    },
    onReachEnd() {
      setEnd(true);
    },
    onReachBeginning() {
      setBeginning(true);
    },
    onFromEdge({ isEnd, isBeginning }) {
      setEdges(isBeginning, setBeginning, isEnd, setEnd);
    },
    onBeforeResize,
    onResize,
    onSetTranslate,
    onAfterInit(swiper) {
      setIsLocked(swiper?.isLocked);
      const tid = setTimeout(() => {
        if (typeof swiper !== "undefined") {
          swiper?.update();
          swiper?.slideTo(initialIndex, swiperSpeed);
          // @ts-ignore
          const { isBeginning, isEnd, visibleSlidesIndexes, isLocked } = swiper;
          setEdges(isBeginning, setBeginning, isEnd, setEnd);
          setIsLocked(isLocked);
          if (typeof handleItemImpression === "function") {
            if (visibleSlidesIndexes?.length)
              handleVisibleImpressions(visibleSlidesIndexes);
          }
          // Run passed in Callback 
          typeof onAfterInit === "function" && onAfterInit(swiper);
        }
      }, 100);
      setTimeoutId(tid);
    },
  } as SwiperProps);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && timeoutId) {
        window.clearTimeout(timeoutId);
        onResize.cancel();
      }
    };
  }, [timeoutId]);

  useEffect(() => {
    setConfig({
      modules: swiperModules,
      a11y,
      breakpoints,
      navigation: {
        nextEl: `#${navBtns.nextElId}`,
        prevEl: `#${navBtns.prevElId}`,
      },
      pagination:
        showPagination || forcePagination
          ? {
              dynamicBullets: true,
              clickable: true,
              el: `.${paginationClass}`,
            }
          : { enabled: false },
      slidesPerView: "auto",
      spaceBetween: itemSpacing,
      initialSlide: initialIndex,
      // freeMode: { enabled: true, sticky: true, momentumBounce: false, minimumVelocity: .3 },
      keyboard: { enabled: false, onlyInViewport: true },
      mousewheel: {
        eventsTarget: `.cui__carousel.swiper-slide.cui__carousel_${randStr.current}`,
        enabled: !disableMouseWheel,
      },
      speed: swiperSpeed,
      focusableElements,
      resizeObserver: true,
      watchSlidesProgress: true,
      cssMode: _cssMode,
      ..._leftPadding,
      ..._rightPadding,
      ...touchPrevention,
      onSwiper(swiper: SwiperType) {
        const { isBeginning, isEnd, isLocked } = swiper;
        setEdges(isBeginning, setBeginning, isEnd, setEnd);
        setIsLocked(isLocked);
        swiperInstance.current = swiper;
      },
      onTouchStart,
      onBreakpoint(swiper: SwiperType) {
        const { currentBreakpoint } = swiper;
        setBreakpoint(currentBreakpoint);
      },
      onSlideChange({
        isEnd,
        isBeginning,
        activeIndex,
        visibleSlidesIndexes = [],
        realIndex,
        ...swiper
      }: SwiperType & { visibleSlidesIndexes?: number[] }) {
        if (typeof onItemChange === "function") {
          onItemChange(activeIndex);
        }
        if (typeof handleItemImpression === "function") {
          if (visibleSlidesIndexes.length)
            handleVisibleImpressions(visibleSlidesIndexes);
        }
      },
      onSetTranslate,
      onReachEnd() {
        setEnd(true);
      },
      onReachBeginning() {
        setBeginning(true);
      },
      onFromEdge({ isEnd, isBeginning }) {
        setEdges(isBeginning, setBeginning, isEnd, setEnd);
      },
      onBeforeResize,
      onResize,
      onAfterInit(swiper) {
        setIsLocked(swiper?.isLocked);
        const tid = setTimeout(() => {
          if (typeof swiper !== "undefined") {
            swiper?.update();
            swiper?.slideTo(initialIndex, swiperSpeed);
            // @ts-ignore
            const { isBeginning, isEnd, visibleSlidesIndexes, isLocked } =
              swiper;
            setEdges(isBeginning, setBeginning, isEnd, setEnd);
            setIsLocked(isLocked);
            if (typeof handleItemImpression === "function") {
              if (visibleSlidesIndexes?.length)
                handleVisibleImpressions(visibleSlidesIndexes);
            }
            // Run passed in Callback 
            typeof onAfterInit === "function" && onAfterInit(swiper);
          }
        }, 100);
        setTimeoutId(tid);
      },
    } as SwiperProps);
  }, [
    centerSlides,
    handleItemImpression,
    onItemChange,
    showPagination,
    forcePagination,
    paginationClass,
    itemSpacing,
    navBtns,
    cssMode,
    disableFreeMode,
    leftPadding,
    rightPadding,
    initialIndex,
    _enabled,
  ]);

  return { config, swiperInstance, isEnd, isBeginning, breakpoint, isLocked };
};
